import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { whatsappLink, instagram, googleMapsLink, email, gymInfo } from '@/mocks/gymInfo';

export default function Footer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [emailValue, setEmailValue] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (emailValue.trim()) {
      const message = `${t('footer.newsletter')}\n${t('footer.emailLabel')}: ${emailValue.trim()}`;
      window.open(`${whatsappLink}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
      setSubscribed(true);
      setEmailValue('');
    }
  };

  const handleNav = (href: string) => {
    const hashIndex = href.indexOf('#');
    if (hashIndex !== -1) {
      const id = href.slice(hashIndex + 1);
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        navigate('/');
        setTimeout(() => {
          const target = document.getElementById(id);
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
      }
      return;
    }
    navigate(href);
  };

  return (
    <footer className="bg-foreground-950 text-background-50" role="contentinfo">
      <div className="w-full px-4 md:px-6 pt-12 md:pt-16 pb-8">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8 mb-10">
          <div className="flex items-center gap-3">
            <span className="font-heading text-2xl md:text-3xl tracking-wider">TOP GYM</span>
            <span className="font-body text-sm text-background-400">Cumbuco</span>
          </div>
          <p className="font-body text-sm text-background-400 max-w-md">
            {t('footer.description')}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          <div>
            <h4 className="font-heading text-lg text-background-50 mb-4">{t('footer.modalities')}</h4>
            <div className="space-y-2">
              <button onClick={() => handleNav('/modalidades')} className="block text-sm font-body text-background-400 hover:text-primary-500 transition-colors">{t('footer.modalityWeightTraining')}</button>
              <button onClick={() => handleNav('/modalidades')} className="block text-sm font-body text-background-400 hover:text-primary-500 transition-colors">{t('footer.modalityCrossTraining')}</button>
              <button onClick={() => handleNav('/modalidades')} className="block text-sm font-body text-background-400 hover:text-primary-500 transition-colors">{t('footer.modalityFunctional')}</button>
              <button onClick={() => handleNav('/modalidades')} className="block text-sm font-body text-background-400 hover:text-primary-500 transition-colors">{t('footer.modalityMuayThai')}</button>
              <button onClick={() => handleNav('/#loja')} className="block text-sm font-body text-background-400 hover:text-primary-500 transition-colors">{t('footer.storeLink')}</button>
            </div>
          </div>
          <div>
            <h4 className="font-heading text-lg text-background-50 mb-4">{t('footer.institutional')}</h4>
            <div className="space-y-2">
              <button onClick={() => handleNav('/')} className="block text-sm font-body text-background-400 hover:text-primary-500 transition-colors">{t('nav.home')}</button>
              <button onClick={() => handleNav('/estrutura')} className="block text-sm font-body text-background-400 hover:text-primary-500 transition-colors">{t('nav.structure')}</button>
              <button onClick={() => handleNav('/#planos')} className="block text-sm font-body text-background-400 hover:text-primary-500 transition-colors">{t('nav.plans')}</button>
              <button onClick={() => handleNav('/horarios')} className="block text-sm font-body text-background-400 hover:text-primary-500 transition-colors">{t('nav.schedules')}</button>
              <button onClick={() => handleNav('/localizacao')} className="block text-sm font-body text-background-400 hover:text-primary-500 transition-colors">{t('nav.location')}</button>
            </div>
          </div>
          <div>
            <h4 className="font-heading text-lg text-background-50 mb-4">{t('footer.contact')}</h4>
            <div className="space-y-2">
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="block text-sm font-body text-background-400 hover:text-primary-500 transition-colors">WhatsApp</a>
              <a href={`mailto:${email}`} className="block text-sm font-body text-background-400 hover:text-primary-500 transition-colors">E-mail</a>
              <a href={instagram} target="_blank" rel="noopener noreferrer" className="block text-sm font-body text-background-400 hover:text-primary-500 transition-colors">Instagram</a>
              <a href={googleMapsLink} target="_blank" rel="noopener noreferrer" className="block text-sm font-body text-background-400 hover:text-primary-500 transition-colors">Google Maps</a>
            </div>
          </div>
          <div>
            <h4 className="font-heading text-lg text-background-50 mb-4">{t('footer.newsletter')}</h4>
            {subscribed ? (
              <p className="text-sm font-body text-primary-500">{t('page.contato.sentBtn')}</p>
            ) : (
              <form onSubmit={handleSubscribe} className="flex flex-col gap-2">
                <label htmlFor="newsletter-email" className="sr-only">{t('footer.emailLabel')}</label>
                <input
                  id="newsletter-email"
                  type="email"
                  value={emailValue}
                  onChange={(e) => setEmailValue(e.target.value)}
                  placeholder={t('footer.emailPlaceholder')}
                  className="w-full px-3 py-2 bg-foreground-900 border border-foreground-800 rounded-lg text-sm font-body text-background-50 placeholder:text-background-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
                <button
                  type="submit"
                  className="btn-hover w-full bg-accent-500 hover:bg-accent-600 text-background-50 font-body text-sm font-semibold px-4 py-2 rounded-lg"
                >
                  {t('footer.newsletterBtn')}
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="border-t border-foreground-900/50 pt-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            <div className="hidden md:block">
              <img
                src="https://storage.readdy-site.link/project_files/1a1c8768-7964-48a6-af85-b4ce5b39e023/edfbf516-fa9d-486d-87f9-9f52c9c1de2b_topgym-estrutura1.webp?v=52881ca8ffdce80104f87fedcc2d51ca"
                alt="Top Gym Cumbuco"
                loading="lazy"
                fetchPriority="low"
                width="400"
                height="128"
                decoding="async"
                className="w-full h-32 object-cover rounded-lg"
              />
            </div>
            <div className="text-center">
              <p className="font-heading text-lg text-background-50 leading-tight">
                {t('footer.slogan')}
              </p>
            </div>
            <div className="flex flex-col items-center md:items-end gap-2">
              <div className="flex items-center gap-4">
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="text-background-400 hover:text-primary-500 transition-colors text-sm underline">
                  WhatsApp
                </a>
                <a href={instagram} target="_blank" rel="noopener noreferrer" className="text-background-400 hover:text-primary-500 transition-colors text-sm underline">
                  Instagram
                </a>
                <a href={googleMapsLink} target="_blank" rel="noopener noreferrer" className="text-background-400 hover:text-primary-500 transition-colors text-sm underline">
                  Maps
                </a>
              </div>
              <p className="text-xs font-body text-background-600">
                © {new Date().getFullYear()} {gymInfo.name}. {t('footer.rights')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}